import type { DetectOptions, DetectionResult, TerminalDetails } from './types';
import { detectFromEnvResult } from './env';
import { getColorLevel, getColorSupport } from './color';
import { isAmbiguousEnvResult } from './normalize';
import { detectFromParentProcessResult } from './parent';
import { detectFromProcessTitleResult } from './process-title';
import { detectFromShellResult } from './shell';

const getTerminalResult = (options: DetectOptions): DetectionResult => {
  const envTerminal = detectFromEnvResult(options);
  let result = envTerminal;

  if (
    options.resolveAmbiguousFromParent &&
    isAmbiguousEnvResult(envTerminal?.terminal ?? null, options)
  ) {
    result = detectFromParentProcessResult(options) ?? result;
  }

  result ??= detectFromShellResult(options);
  result ??= detectFromProcessTitleResult(options);

  return (
    result ?? {
      terminal: envTerminal?.terminal ?? 'unknown',
      source: envTerminal ? envTerminal.source : 'unknown',
      generic: envTerminal?.generic
    }
  );
};

export const detectTerminalDetails = (options: DetectOptions = {}): TerminalDetails => {
  const env = options.env ?? process.env;
  const stdout = options.stdout ?? process.stdout;
  const isTTY = Boolean(stdout.isTTY);
  const isSSH = Boolean(env.SSH_CONNECTION);
  const term = env.TERM?.trim();
  const termProgram = env.TERM_PROGRAM?.trim();
  const colorTerm = env.COLORTERM?.trim();
  const colorSupport = getColorSupport(env);

  if (!isTTY && !options.unpipe) {
    return {
      terminal: 'none',
      source: 'none',
      isTTY,
      isSSH,
      isAmbiguous: false,
      supportsColor: false,
      colorLevel: 0,
      supports256Color: colorSupport.supports256Color,
      supportsTrueColor: colorSupport.supportsTrueColor,
      term,
      termProgram,
      colorTerm,
      generic: 'none'
    };
  }

  const result = getTerminalResult(options);
  const colorLevel = getColorLevel(env, result.terminal, colorSupport);

  return {
    terminal: result.terminal,
    source: result.source,
    isTTY,
    isSSH,
    isAmbiguous: result.source === 'env' && isAmbiguousEnvResult(result.terminal, options),
    colorLevel,
    supportsColor: colorLevel > 0,
    supports256Color: colorSupport.supports256Color,
    supportsTrueColor: colorSupport.supportsTrueColor,
    term,
    termProgram,
    colorTerm,
    generic: result.generic ?? term?.toLowerCase() ?? 'unknown',
    pid: result.pid,
    appName: result.appName
  };
};

export const detectTerminal = (options: DetectOptions = {}): string => {
  return detectTerminalDetails(options).terminal;
};
